
import { createContext, useContext } from 'react';

export type Language = 'en' | 'de';

export const DEFAULT_LEVEL_TITLES = [
    "Wanderer", "Dreamer", "Novice", "Apprentice", "Student", 
    "Initiate", "Seeker", "Explorer", "Adventurer", "Pathfinder",
    "Scout", "Ranger", "Guide", "Pioneer", "Voyager",
    "Nomad", "Wayfarer", "Pilgrim", "Strategist", "Planner",
    "Architect", "Builder", "Maker", "Creator", "Artisan",
    "Craftsman/Craftswoman", "Specialist", "Expert", "Professional", "Mentor",
    "Teacher", "Scholar", "Sage", "Guru", "Master",
    "Grandmaster", "Champion", "Hero/Heroine", "Guardian", "Protector",
    "Knight/Dame", "Lord/Lady", "Baron/Baroness", "Duke/Duchess", "Prince/Princess",
    "King/Queen", "Emperor/Empress", "Legend", "Myth", "Eternal"
];

export const DEFAULT_LEVEL_TITLES_DE = [
    "Wanderer", "Träumer", "Neuling", "Lehrling", "Schüler",
    "Eingeweihter", "Suchender", "Entdecker", "Abenteurer", "Pfadfinder",
    "Späher", "Waldläufer", "Wegweiser", "Pionier", "Reisender",
    "Nomade", "Weltenbummler", "Pilger", "Stratege", "Planer",
    "Architekt", "Baumeister", "Macher", "Schöpfer", "Kunsthandwerker",
    "Handwerksmeister", "Spezialist", "Experte", "Profi", "Mentor",
    "Lehrer", "Gelehrter", "Weiser", "Guru", "Meister",
    "Großmeister", "Champion", "Held/Heldin", "Wächter", "Beschützer",
    "Ritter/Dame", "Lord/Lady", "Baron/Baronin", "Herzog/Herzogin", "Prinz/Prinzessin",
    "König/Königin", "Kaiser/Kaiserin", "Legende", "Mythos", "Ewiger"
];

export const translations = {
  en: {
    appName: "LifeBuckets",
    login: "Log In",
    signup: "Sign Up",
    logout: "Log Out",
    username: "Username",
    password: "Password",
    email: "Email Address",
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    welcomeBack: "Welcome back again",
    getStarted: "Start your journey",
    invalidLogin: "Invalid username or password",
    accountCreated: "Account created successfully",
    userProfile: "User Profile",
    signedInAs: "Signed in as",
    newBucket: "New Bucket",
    viewMode: "View Mode",
    gridView: "Grid View",
    listView: "List View",
    statistics: "Statistics",
    calendarView: "Calendar",
    allTasksView: "All Tasks",
    ideasView: "Ideas & Notes",
    preferences: "Preferences",
    preferencesDesc: "App Customization",
    levelNames: "Level Names",
    levelNamesDesc: "Customize the titles for your level progression.",
    resetToDefault: "Reset to Default",
    savePreferences: "Save Preferences",
    preferencesSaved: "Preferences saved successfully",
    ideaPlaceholder: "Capture an idea, bug report, or thought...",
    noIdeas: "No ideas captured yet.",
    reminders: "Reminders",
    active: "active",
    allCaughtUp: "All caught up!",
    reminderText: (remaining: number, target: number) => `You need ${remaining} more points this month to reach your goal of ${target}.`,
    scheduledTaskReminder: (count: number) => `You have ${count} tasks scheduled for today.`,
    introTitlePrefix: "Fill your life with ",
    introTitleHighlight: "progress",
    introTitleSuffix: ".",
    introSubtitle: "Set goals, assign points to tasks, and fill your buckets before the deadline.",
    yourBuckets: "Your Buckets",
    statsOverview: "Statistics Overview",
    createFirstBucket: "Create your first bucket",
    noBucketsFound: "No buckets found. Switch to grid view or click New Bucket to start.",
    editBucket: "Edit Bucket",
    createBucketTitle: "Create New Bucket",
    editBucketTitle: "Edit Bucket",
    bucketName: "Bucket Name",
    placeholderBucketName: "e.g. Work, Fitness, Learning",
    colorTheme: "Color Theme",
    totalGoal: "Total Goal (Fill the Bucket)",
    milestoneName: "Milestone Name",
    placeholderMilestone: "e.g. Become Fluent in Spanish",
    targetPoints: "Target Points",
    deadline: "Deadline",
    monthlyGoal: "Monthly Recurring Goal",
    pointsPerMonth: "Points Per Month",
    pointsMinimum: "points minimum",
    monthlyGoalDesc: "The app will remind you if you fall behind this monthly target.",
    saveChanges: "Save Changes",
    createBucketAction: "Create Bucket",
    deleteBucketConfirm: "Are you sure you want to delete this bucket?",
    daysLeft: (days: number) => `${days} days left`,
    ended: "Ended",
    milestoneReached: "Milestone Reached!",
    overflowing: (target: number) => `You've hit ${target} points. Outstanding work!`,
    nextMilestoneName: "Next Milestone Name",
    nextMilestonePlaceholder: "e.g. Phase 2",
    addPoints: "Add Points",
    newDeadlineOpt: "New Deadline (Opt)",
    levelUp: "Level Up & Continue",
    or: "or",
    completeGoal: "Complete (Remove Goal)",
    emptyBucket: "Empty bucket.\nAdd a task.",
    pourSomething: "Pour something in...",
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly",
    repeat: "Repeat",
    noRepeat: "No Repeat",
    tasks: "Tasks",
    goalReached: "GOAL REACHED",
    openTasks: "Open Tasks",
    doneTasks: "Done Tasks",
    pointsEarned: "Points Earned",
    completionRate: "Completion Rate",
    bucketProgress: "Bucket Levels",
    basedOnPoints: "Based on points completed",
    done: "done",
    focusDistribution: "Focus Distribution",
    allTimePoints: "All time points",
    totalEffort: (share: number) => `${Math.round(share)}% of total effort`,
    completeTasksToSee: "Complete tasks to see your focus distribution.",
    noBucketsCreated: "No buckets created yet.",
    next: "Next",
    pts: "pts",
    required: "*",
    monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    dayNamesShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    milestoneHistory: "Milestone History",
    reachedOn: "Reached on",
    noMilestones: "No milestones reached yet.",
    startTimer: "Start working",
    stopTimer: "Stop working",
    workingOn: "Working on",
    sessionTime: "Session Time",
    finish: "Finish",
    switchTaskTitle: "Switch Task?",
    switchTaskMessage: "The current task will be stopped. Do you want to switch to",
    confirmSwitch: "Yes, Switch",
    cancel: "Cancel",
    coverImage: "Cover Image URL",
    coverImagePlaceholder: "https://example.com/image.jpg (Optional)",
    scheduledDate: "Scheduled Date",
    today: "Today",
    tomorrow: "Tomorrow",
    moreTasks: (count: number) => `+${count} more`,
    viewMonth: "Month",
    viewWeek: "Week",
    viewDay: "Day",
    bucketType: "Bucket Type",
    typeStandard: "Standard",
    typeFokusSprint: "Fokus Sprint",
    fokusSprintDesc: "A goal-driven mission for a specific event. Has a strict start and end date.",
    startDate: "Start Date",
    endDate: "End Date",
    bucketClosed: "Closed",
    bucketClosedDesc: "This bucket is closed because the time has passed. Edit the date to reopen.",
    level: "Level",
    levelUpMessage: "Level Up! Keep going!",
    levelTitles: DEFAULT_LEVEL_TITLES,
    addTask: "Add Task",
    editTask: "Edit Task",
    taskName: "Task Description",
    difficulty: "Difficulty",
    timeConsumption: "Time Consumption",
    diffEasy: "Easy",
    diffMedium: "Medium",
    diffHard: "Hard",
    timeShort: "Short",
    timeMedium: "Medium",
    timeLong: "Long",
    calculatedPoints: "Total Points",
    saveTask: "Save Task",
    createTask: "Create Task",
    totalLevels: "Total Levels",
    complexityAnalysis: "Task Complexity",
    difficultyDist: "Difficulty Distribution",
    timeDist: "Time Distribution",
    alsoCountsFor: "Also counts for...",
    linkedFrom: "Linked from",
    sortBy: "Sort by",
    sortDate: "Date",
    sortPoints: "Points",
    sortBucket: "Bucket",
    appDesigns: "Designs",
    appDesignsDesc: "Change the visual appearance of the application.",
    originalDesign: "Original Design",
    originalDesignDesc: "The classic LifeBuckets aesthetic with deep slates and indigo accents.",
    experimentalOriginalDesign: "Experimental Original",
    experimentalOriginalDesignDesc: "A playground version of the original design for trying new features.",
    flatDesign: "Flat Design",
    flatDesignDesc: "High-contrast colorful blocks with custom Primary and Secondary colors.",
    minimalistDesign: "Minimalist (Future)",
    glassmorphismDesign: "Glassmorphism (Future)",
    neoBrutalismDesign: "Neo-Brutalismus (Future)",
    currentDesign: "Current Design",
    repetitions: "Repetitions",
    timesPerPeriod: "Times per period",
    sessionsDone: (current: number, total: number) => `${current} / ${total} sessions done`,
    description: "Description",
    placeholderDescription: "e.g. I want to make my house christmas ready..."
  },
  de: {
    appName: "LifeBuckets",
    login: "Anmelden",
    signup: "Registrieren",
    logout: "Abmelden",
    username: "Benutzername",
    password: "Passwort",
    email: "E-Mail-Adresse",
    dontHaveAccount: "Noch kein Konto?",
    alreadyHaveAccount: "Bereits ein Konto?",
    welcomeBack: "Willkommen wieder zurück",
    getStarted: "Reise beginnen",
    invalidLogin: "Ungültiger Benutzername oder Passwort",
    accountCreated: "Konto erfolgreich erstellt",
    userProfile: "Benutzerprofil",
    signedInAs: "Angemeldet als",
    newBucket: "Neuer Bucket",
    viewMode: "Ansichtsmodus",
    gridView: "Raster",
    listView: "Liste",
    statistics: "Statistik",
    calendarView: "Kalender",
    allTasksView: "Alle Aufgaben",
    ideasView: "Ideen & Notizen",
    preferences: "Einstellungen",
    preferencesDesc: "App-Anpassung",
    levelNames: "Level-Namen",
    levelNamesDesc: "Passe die Titel für deinen Level-Fortschritt an.",
    resetToDefault: "Zurücksetzen",
    savePreferences: "Speichern",
    preferencesSaved: "Einstellungen gespeichert",
    ideaPlaceholder: "Idee, Bug oder Gedanke festhalten...",
    noIdeas: "Noch keine Ideen erfasst.",
    reminders: "Erinnerungen",
    active: "aktiv",
    allCaughtUp: "Alles erledigt!",
    reminderText: (remaining: number, target: number) => `Du benötigst noch ${remaining} Punkte diesen Monat, um dein Ziel von ${target} zu erreichen.`,
    scheduledTaskReminder: (count: number) => `Du hast heute ${count} geplante Aufgaben.`,
    introTitlePrefix: "Fülle dein Leben mit ",
    introTitleHighlight: "Fortschritt",
    introTitleSuffix: ".",
    introSubtitle: "Setze Ziele, vergib Punkte und fülle deine Buckets vor Ablauf der Frist.",
    yourBuckets: "Deine Buckets",
    statsOverview: "Statistik-Übersicht",
    createFirstBucket: "Ersten Bucket erstellen",
    noBucketsFound: "Keine Buckets gefunden. Erstelle einen, um loszulegen.",
    editBucket: "Bucket bearbeiten",
    createBucketTitle: "Neuen Bucket erstellen",
    editBucketTitle: "Bucket bearbeiten",
    bucketName: "Name des Buckets",
    placeholderBucketName: "z.B. Arbeit, Fitness, Lernen",
    colorTheme: "Farbthema",
    totalGoal: "Gesamtziel (Bucket füllen)",
    milestoneName: "Name des Meilensteins",
    placeholderMilestone: "z.B. Spanisch fließend sprechen",
    targetPoints: "Zielpunkte",
    deadline: "Frist",
    monthlyGoal: "Monatliches Ziel",
    pointsPerMonth: "Punkte pro Monat",
    pointsMinimum: "Mindestpunkte",
    monthlyGoalDesc: "Die App erinnert dich, wenn du dein monatliches Ziel aus den Augen verlierst.",
    saveChanges: "Speichern",
    createBucketAction: "Bucket erstellen",
    deleteBucketConfirm: "Möchtest du diesen Bucket wirklich löschen?",
    daysLeft: (days: number) => `Noch ${days} Tage`,
    ended: "Beendet",
    milestoneReached: "Meilenstein erreicht!",
    overflowing: (target: number) => `Du hast ${target} Punkte erreicht. Großartige Arbeit!`,
    nextMilestoneName: "Nächster Meilenstein",
    nextMilestonePlaceholder: "z.B. Phase 2",
    addPoints: "Punkte hinzufügen",
    newDeadlineOpt: "Neue Frist (optional)",
    levelUp: "Level Up & Weiter",
    or: "oder",
    completeGoal: "Ziel abschließen",
    emptyBucket: "Bucket ist leer.\nFüge eine Aufgabe hinzu.",
    pourSomething: "Was steht heute an?...",
    daily: "Täglich",
    weekly: "Wöchentlich",
    monthly: "Monatlich",
    repeat: "Wiederholung",
    noRepeat: "Keine",
    tasks: "Aufgaben",
    goalReached: "ZIEL ERREICHT",
    openTasks: "Offene Aufgaben",
    doneTasks: "Erledigte Aufgaben",
    pointsEarned: "Verdiente Punkte",
    completionRate: "Abschlussrate",
    bucketProgress: "Bucket Level",
    basedOnPoints: "Basierend auf Punkten",
    done: "erledigt",
    focusDistribution: "Fokus-Verteilung",
    allTimePoints: "Gesamtpunkte",
    totalEffort: (share: number) => `${Math.round(share)}% des Gesamtaufwands`,
    completeTasksToSee: "Erledige Aufgaben, um Statistiken zu sehen.",
    noBucketsCreated: "Noch keine Buckets erstellt.",
    next: "Weiter",
    pts: "Pkt",
    required: "*",
    monthNames: ["Januar", "Februar", "März", "April", "May", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
    dayNamesShort: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    milestoneHistory: "Meilenstein-Historie",
    reachedOn: "Erreicht am",
    noMilestones: "Noch keine Meilensteine erreicht.",
    startTimer: "Timer starten",
    stopTimer: "Timer stoppen",
    workingOn: "In Arbeit",
    sessionTime: "Sitzungsdauer",
    finish: "Abschließen",
    switchTaskTitle: "Aufgabe wechseln?",
    switchTaskMessage: "Die aktuelle Aufgabe wird gestoppt. Wechseln zu",
    confirmSwitch: "Ja, wechseln",
    cancel: "Abbrechen",
    coverImage: "Titelbild-URL",
    coverImagePlaceholder: "https://beispiel.de/bild.jpg (Optional)",
    scheduledDate: "Geplantes Datum",
    today: "Heute",
    tomorrow: "Morgen",
    moreTasks: (count: number) => `+${count} weitere`,
    viewMonth: "Monat",
    viewWeek: "Woche",
    viewDay: "Tag",
    bucketType: "Bucket-Typ",
    typeStandard: "Standard",
    typeFokusSprint: "Fokus Sprint",
    fokusSprintDesc: "Eine zielgerichtete Mission für ein Ereignis. Hat ein festes Start- und Enddatum.",
    startDate: "Startdatum",
    endDate: "Enddatum",
    bucketClosed: "Geschlossen",
    bucketClosedDesc: "Dieser Bucket ist geschlossen. Bearbeite das Datum, um ihn zu reaktivieren.",
    level: "Level",
    levelUpMessage: "Level Up! Weiter so!",
    levelTitles: DEFAULT_LEVEL_TITLES_DE,
    addTask: "Aufgabe hinzufügen",
    editTask: "Aufgabe bearbeiten",
    taskName: "Aufgabenbeschreibung",
    difficulty: "Schwierigkeit",
    timeConsumption: "Zeitaufwand",
    diffEasy: "Leicht",
    diffMedium: "Mittel",
    diffHard: "Schwer",
    timeShort: "Kurz",
    timeMedium: "Mittel",
    timeLong: "Lang",
    calculatedPoints: "Gesamtpunkte",
    saveTask: "Speichern",
    createTask: "Erstellen",
    totalLevels: "Gesamtlevel",
    complexityAnalysis: "Komplexitätsanalyse",
    difficultyDist: "Schwierigkeitsverteilung",
    timeDist: "Zeitverteilung",
    alsoCountsFor: "Zählt auch für...",
    linkedFrom: "Verlinkt von",
    sortBy: "Sortieren nach",
    sortDate: "Datum",
    sortPoints: "Punkte",
    sortBucket: "Bucket",
    appDesigns: "Designs",
    appDesignsDesc: "Ändere das visuelle Erscheinungsbild der Anwendung.",
    originalDesign: "Original Design",
    originalDesignDesc: "Die klassische LifeBuckets Ästhetik mit tiefen Schiefertönen und Indigo-Akzenten.",
    experimentalOriginalDesign: "Experimentelles Original",
    experimentalOriginalDesignDesc: "Eine Spielwiese des Originaldesigns zum Testen neuer Funktionen.",
    flatDesign: "Flat Design",
    flatDesignDesc: "Farbenfrohe Blöcke mit hohem Kontrast und benutzerdefiniertem Primär- und Sekundär-Farbpaar.",
    minimalistDesign: "Minimalistisch (Zukunft)",
    glassmorphismDesign: "Glassmorphismus (Zukunft)",
    neoBrutalismDesign: "Neo-Brutalismus (Zukunft)",
    currentDesign: "Aktuelles Design",
    repetitions: "Wiederholungen",
    timesPerPeriod: "Mal pro Zeitraum",
    sessionsDone: (current: number, total: number) => `${current} / ${total} Sitzungen erledigt`,
    description: "Beschreibung",
    placeholderDescription: "z.B. Ich möchte mein Haus weihnachtlich herrichten..."
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.en;
}

export const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: translations.en
});

export const useLanguage = () => useContext(LanguageContext);
